/**
 * Route copy for `src/routes/content` (keys: `routes.content.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  aiMediaStudioPage: {
    inpainting: 'Inpainting',
    localization: 'Localization',
    notAvailable: 'Not available —',
    aiMediaStudio: 'AI media studio',
    inpaintingFillEraseAnAreaAnd: 'Inpainting (fill / erase an area) and one-click localization (per-network variants). Voice-over, cloned voice and avatar live in the Video & audio studio.',
    tool: 'Tool',
    needAVoiceOverACloned: 'Need a voice-over, a cloned voice or an avatar?',
    openTheVideoAudioStudio: 'Open the Video & audio studio',
    imageRetouchedSavedToTheMedia: 'Image retouched — saved to the media library.',
    inpaintingFailed: 'Inpainting failed.',
    mediaIdImage: 'Media ID (image)',
    orAnImageUrlNextTo: 'OR an image URL next to it to enable the brush',
    idOfATenantImage: 'ID of a tenant image',
    imageUrl: 'Image URL',
    httpsPhotoPng: 'https://…/photo.png',
    brush: 'Brush',
    brushSize: 'Brush size',
    brushsizePx: '{{brushSize}}px',
    clearTheMask: 'Clear the mask',
    imageToRetouch: 'Image to retouch',
    paintTheAreaToRetouchIn: 'Paint the area to retouch in white (the rest is preserved).',
    enterAnImageUrlToPaint: 'Enter an image URL to paint the mask directly, or upload a PNG mask below (white = area to retouch).',
    pngMaskOptionalReplacesTheBrush: 'PNG mask (optional — replaces the brush)',
    maskUploadedItWillBeUsed: 'Mask uploaded — it will be used.',
    prompt: 'Prompt',
    whatShouldFillTheAreaRequired: 'What should fill the area (required)',
    optionalInEraseMode: 'Optional in erase mode',
    eGAClearBlueSky: 'e.g. a clear blue sky',
    leaveEmptyToErase: '(leave empty to erase)',
    mode: 'Mode',
    fill: 'Fill',
    erase: 'Erase',
    processing: 'Processing…',
    fillTheArea: 'Fill the area',
    eraseTheArea: 'Erase the area',
    aiOperationDeductedFromYourCredits: 'AI operation — deducted from your credits (no key ⇒ no charge).',
    result: 'Result',
    retouchedImage: 'Retouched image',
    open: 'Open',
    download: 'Download',
    enterTheBaseCaptionToLocalize: 'Enter the base caption to localize.',
    chooseAtLeastOneTargetLanguage: 'Choose at least one target language.',
    partialLocalizationLengthVV2: 'Partial localization ({{length}}/{{v}}) — {{v2}}.',
    lengthLanguageSLocalized: '{{length}} language(s) localized.',
    localizationFailed: 'Localization failed.',
    baseCaption: 'Base caption',
    theSourceCaptionToLocalizeFor: 'The source caption to localize for every network…',
    targetLanguages: 'Target languages',
    enterToAddEGEn: 'Enter to add — e.g. en, es, de-DE',
    enEsDe: 'en, es, de…',
    removeL: 'Remove {{l}}',
    perNetworkVariants: 'Per-network variants',
    oneAdaptedVariantPerCheckedPlatform: 'One adapted variant per checked platform',
    sourceLanguageOptional: 'Source language (optional)',
    auto: 'auto',
    toneOptional: 'Tone (optional)',
    professionalCasual: 'professional, casual…',
    hashtagsMentions: 'Hashtags / mentions',
    keepAsIs: 'Keep as-is',
    localizing: 'Localizing…',
    localizeInOneClick: 'Localize in one click',
    oneAiCallPerLanguageDeducted: 'One AI call per language — deducted from your credits ({{v}} language',
    languageLocale: 'Language: {{locale}}',
    localizedCaption: 'Localized caption',
    empty: '(empty)',
    copyTheCaption: 'Copy the caption',
    copied: 'Copied ✓',
    couldNotCopy: 'Could not copy — select and copy the text manually.',
    sendToTheComposer: 'Send to the composer',
  },
  approvalChainSettingsPage: {
    stepVAddAtLeastOne: 'Step {{v}}: add at least one allowed role or a named approver.',
    approvalWorkflowSaved: 'Approval workflow saved.',
    savingTheWorkflowFailed: 'Saving the workflow failed.',
    notificationsSaved: 'Notifications saved.',
    savingTheNotificationsFailed: 'Saving the notifications failed.',
    approvals: 'Approvals',
    configureTheContentApprovalWorkflowAnd: 'Configure the content approval workflow and Slack / Teams notifications.',
    theApprovalWorkflowIsNotDeployed: 'The approval workflow is not deployed on this environment yet. You can prepare the configuration below; saving will be available as soon as it is deployed.',
    approvalSections: 'Approval sections',
    approvalWorkflow: 'Approval workflow',
    notifications: 'Notifications',
    loading: 'Loading…',
    enableMultiStepApprovalForThis: 'Enable multi-step approval for this account',
    noStepsAddAFirstStep: 'No steps. Add a first step to build the workflow.',
    moveStepUp: 'Move step up',
    moveStepDown: 'Move step down',
    delete: 'Delete',
    stepName: 'Step name',
    eGLegalReview: 'e.g. Legal review',
    rule: 'Rule',
    allowedRolesCommaSeparated: 'Allowed roles (comma-separated)',
    ownerAdminEditor: 'owner, admin, editor',
    namedApproversUserId: 'Named approvers (user ID)',
    allNamedApproversMustApproveUnanimous: 'All named approvers must approve (unanimous).',
    aSingleApprovalFromAnEligible: 'A single approval from an eligible approver is enough.',
    addAStep: '+ Add a step',
    saving: 'Saving…',
    saveTheWorkflow: 'Save the workflow',
    slackWebhookIncomingWebhookUrl: 'Slack webhook (Incoming Webhook URL)',
    httpsHooksSlackComServices: 'https://hooks.slack.com/services/…',
    microsoftTeamsWebhookIncomingWebhookUrl: 'Microsoft Teams webhook (Incoming Webhook URL)',
    httpsOutlookOfficeComWebhook: 'https://outlook.office.com/webhook/…',
    notificationsSentContentAwaitingAStep: 'Notifications sent: content awaiting a step, approval, rejection and @mentions in comments. Without a configured webhook, no notification is sent.',
    saveNotifications: 'Save notifications',
  },
  carouselEditorPage: {
    errorStatus: 'Error {{status}}',
    newCarousel: 'New carousel',
    slide1: 'Slide 1',
    slide2: 'Slide 2',
    couldNotLoadTheTemplates: 'Could not load the templates.',
    couldNotOpenTheCarousel: 'Could not open the carousel.',
    theOperationFailed: 'The operation failed.',
    selectACarouselTemplate: 'Select a carousel template.',
    carouselEditor: 'Carousel editor',
    createEditAndExportAMulti: 'Create, edit and export a multi-slide carousel.',
    theCarouselServiceIsNotDeployed: 'The carousel service is not deployed on this environment yet. Try again later.',
    createEditAndExportAMulti2: 'Create, edit and export a multi-slide carousel (PNG per slide + PDF).',
    noCarouselCategoryTemplateExistsCreate: 'No “carousel” category template exists. Create one in the Graphic studio, then come back here to generate and export carousels.',
    carouselName: 'Carousel name',
    model: 'Model',
    brandKitOptional: 'Brand kit (optional)',
    none: 'None',
    creativeBriefOptional: 'Creative brief (optional)',
    guidesTheAiGenerationOfSlides: 'Guides the AI generation of slides left empty and applies to the final text of each slide.',
    slidesLength: 'Slides ({{length}})',
    slideV: 'Slide {{v}}',
    moveUp: 'Move up',
    moveDown: 'Move down',
    delete: 'Delete',
    slideTextLeaveEmptyForAi: 'Slide text (leave empty for AI generation from the brief)',
    saveThisSlide: 'Save this slide',
    addASlide: '+ Add a slide',
    regenerateTheCarousel: 'Regenerate the carousel',
    generateTheCarousel: 'Generate the carousel',
    refreshThePreview: 'Refresh the preview',
    generateTheCarouselToSeeThe: 'Generate the carousel to see the rendered preview.',
    slideUnavailable: 'Slide unavailable',
    viewSlideV: 'View slide {{v}}',
    downloadThePdf: 'Download the PDF',
    slideSExportedPngPdfSaved: 'slide(s) exported — PNG + PDF saved to the media library.',
    carousel: 'Carousel',
  },
  components: {
    loading: 'Loading…',
    underReview: 'Under review',
    approved: 'Approved',
    scheduled: 'Scheduled',
    published: 'Published',
    failed: 'Failed',
    archived: 'Archived',
    justNow: 'just now',
    minsMinAgo: '{{mins}} min ago',
    hoursHAgo: '{{hours}} h ago',
    daysDAgo: '{{days}} d ago',
    bold: 'Bold',
    italic: 'Italic',
    title: 'Title',
    bulletedList: 'Bulleted list',
    list: '• List',
    numberedList: 'Numbered list',
    n1List: '1. List',
    link: 'Link',
    link2: '🔗 Link',
    draft: 'Draft',
  },
  contentEditorPage: {
    couldNotSubmit: 'Could not submit.',
    couldNotApprove: 'Could not approve.',
    title: 'Title',
    body: 'Body',
    theRequestTimedOutTryAgain: 'The request timed out — try again.',
    theContentBodyIsRequired: 'The content body is required.',
    couldNotSaveCheckTheContent: 'Could not save (check the content status).',
    couldNotArchive: 'Could not archive.',
    couldNotDeletePermanently: 'Could not delete permanently.',
    couldNotReject: 'Could not reject.',
    couldNotAddTheMediaStatus: 'Could not add the media (status not editable?).',
    couldNotDeleteTheMedia: 'Could not delete the media.',
    couldNotAddTheGeneratedImages: 'Could not add the generated images (status not editable?).',
    couldNotSchedule: 'Could not schedule.',
    couldNotComputeTheViralityScore: 'Could not compute the virality score.',
    loadingTheContent: 'Loading the content…',
    contentNotFound: 'Content not found',
    backToTheList: 'Back to the list',
    back: 'Back',
    newContent: 'New content',
    editTheContent: 'Edit the content',
    generateWithAi: '✨ Generate with AI',
    readOnly: 'Read-only',
    saving: 'Saving…',
    create: 'Create',
    save: 'Save',
    aCatchyTitle: 'A catchy title…',
    writeYourPostBoldItalicLists: 'Write your post… (**bold**, *italic*, lists)',
    aiAssistant: 'AI assistant:',
    rewriteImproveTheTextWithAi: 'Rewrite / improve the text with AI (credits)',
    improveWithAi: '🪄 Improve with AI',
    generateRelevantHashtagsCredits: 'Generate relevant hashtags (credits)',
    generateHashtags: '# Generate hashtags',
    suggestVisualIdeasCredits: 'Suggest visual ideas (credits)',
    visualSuggestions: '🖼️ Visual suggestions',
    estimateThisDraftSViralityPotential: 'Estimate this draft\'s virality potential (explainable heuristic, no credits)',
    viralityScore: '📈 Virality score',
    firstWriteSomeTextToEnrich: 'First write some text to enrich.',
    targetPlatforms: 'Target platforms',
    addImagesVideosGifsOrFiles: 'Add images, videos, GIFs or files — they will be attached on creation.',
    perNetworkPreview: 'Per-network preview',
    settings: 'Settings',
    turnOnAdvancedModeForHashtags: 'Turn on advanced mode for hashtags, SEO and metadata.',
    advancedOptions: 'Advanced options',
    appliedOnSave: 'Applied on save.',
    hashtags: 'Hashtags',
    commaSeparated: 'comma-separated',
    launchAiSaas: 'launch, ai, saas',
    seoKeywords: 'SEO keywords',
    internalNote: 'Internal note',
    rawMetadata: 'Raw metadata',
    workflow: 'Workflow',
    archive: 'Archive',
    deletePermanently: 'Delete permanently',
    submitForReview: 'Submit for review',
    approve: 'Approve',
    separationOfDutiesTheAuthorCannot: 'Separation of duties: the author cannot approve their own content.',
    enableSelfApprovalForThisAccount: ' Enable self-approval for this account, or have another reviewer approve it.',
    anotherReviewerOwnerAdminMustApprove: ' Another reviewer (owner/admin) must approve.',
    insufficientRightsToChangeTheApproval: 'Insufficient rights to change the approval policy.',
    couldNotEnableSelfApproval: 'Could not enable self-approval.',
    enabling: 'Enabling…',
    allowSelfApproval: 'Allow self-approval',
    reject: 'Reject',
    schedule: 'Schedule…',
    publishNow: 'Publish now',
    noActionAvailableForThisStatus: 'No action available for this status.',
    scheduledV: 'Scheduled: {{v}}',
    publishedV: 'Published: {{v}}',
    versionHistoryLength: 'Version history ({{length}})',
    noEarlierVersion: 'No earlier version.',
    archiveTheContent: 'Archive the content',
    archiveThisContent: 'Archive this content?',
    permanentlyDeleteThisContentThisCannot: 'Permanently delete this content? This cannot be undone: the content is removed from the database (net-zero cleanup).',
    highPotential: 'High potential',
    mediumPotential: 'Medium potential',
    lowPotential: 'Low potential',
    textLength: 'Text length',
    media: 'Media',
    externalLinks: 'External links',
    scheduledSlot: 'Scheduled slot',
    viralityScore2: 'Virality score',
    closeTheViralityScore: 'Close the virality score',
    analyzingTheDraft: 'Analyzing the draft…',
    tryAgain: 'Try again',
    estimatedPotentialExplainableHeuristicNo: 'Estimated potential — explainable heuristic, no reach guarantee.',
    factors: 'Factors',
    suggestions: 'Suggestions',
    french: 'French',
    english: 'English',
    spanish: 'Spanish',
    german: 'German',
    italian: 'Italian',
    portuguese: 'Portuguese',
    fallbackContentAiUnavailableDoNot: 'Fallback content — AI unavailable, do not publish as-is. Regenerate once the AI service is back before posting.',
    aiGenerationTimedOutTheAi: 'AI generation timed out (the AI service did not respond). Try again or close.',
    theAiGenerationServiceIsTemporarily: 'The AI generation service is temporarily unavailable (endpoint not found). Try again later.',
    theAiServiceIsUnavailableUpstream: 'The AI service is unavailable (upstream error). No content was generated — try again later.',
    aiGenerationFailedAiServiceUnavailable: 'AI generation failed (AI service unavailable?).',
    generatedContentPreview: 'Generated content preview',
    useThisContent: 'Use this content',
    checkTheResultBeforeReplacingYour: 'Check the result before replacing your post\'s title and body.',
    generatedImagesLength: 'Generated images ({{length}})',
    visualSuggestions2: 'Visual suggestions',
    videoSuggestionsLength: 'Video suggestions ({{length}})',
    generateWithAi2: 'Generate with AI',
    cancel: 'Cancel',
    generating: 'Generating…',
    generate: 'Generate',
    topic: 'Topic',
    whatIsThePostAbout: 'What is the post about?',
    eGLaunchOfOurNew: 'E.g. launch of our new AI feature',
    tone: 'Tone',
    length: 'Length',
    language: 'Language',
    targetAudienceOptional: 'Target audience (optional)',
    eGB2bMarketingDecisionMakers: 'E.g. B2B marketing decision-makers',
    additionalInstructionsOptional: 'Additional instructions (optional)',
    styleGuidelinesPointsToCover: 'Style guidelines, points to cover…',
    generationOptions: 'Generation options',
    emojis: 'Emojis',
    callToActionCta: 'Call to action (CTA)',
    images: 'Images',
    videoSuggestions: 'Video suggestions',
    statistics: 'Statistics',
    quotes: 'Quotes',
    multiAgentHigherQualitySlower: 'Multi-agent (higher quality, slower)',
    ctaTypeOptional: 'CTA type (optional)',
    eGSignUpLearnMore: 'E.g. Sign up, Learn more',
    numberOfImages: 'Number of images',
    visualStyle: 'Visual style',
    moreConcise: 'More concise',
    shortenThisTextAndMakeIt: 'Shorten this text and make it more concise, without losing the key ideas.',
    punchier: 'Punchier',
    makeThisTextPunchierAndMore: 'Make this text punchier and more engaging for social media.',
    proofread: 'Proofread',
    fixTheSpellingGrammarAndPunctuation: 'Fix the spelling, grammar and punctuation without changing the meaning.',
    moreProfessional: 'More professional',
    rephraseThisTextWithAMore: 'Rephrase this text with a more professional tone.',
    currentText: 'Current text',
    improveAndClarifyThisTextWhile: 'Improve and clarify this text while keeping its meaning and language.',
    aiImprovementFailedServiceUnavailable: 'AI improvement failed (service unavailable?).',
    improvedText: 'Improved text',
    useThisText: 'Use this text',
    close: 'Close',
    theAiServiceCouldNotImprove: 'The AI service could not improve the text (LLM backend unavailable). The original text is returned unchanged.',
    checkTheProposalBeforeReplacingYour: 'Check the proposal before replacing your post\'s body.',
    improveWithAi2: 'Improve with AI',
    improving: 'Improving…',
    improve: 'Improve',
    instruction: 'Instruction',
    howShouldTheAiReworkThe: 'How should the AI rework the text?',
    eGMakeItMoreConcise: 'E.g. make it more concise and add a call to action',
    toneOptional: 'Tone (optional)',
    unchanged: '— unchanged —',
    thisActionUsesAiCredits: 'This action uses AI credits.',
    hashtagGenerationFailedServiceUnavailabl: 'Hashtag generation failed (service unavailable?).',
    generateHashtags2: 'Generate hashtags',
    regenerate: 'Regenerate',
    insertSize: 'Insert ({{size}})',
    numberOfHashtags: 'Number of hashtags',
    noHashtagSuggested: 'No hashtag suggested.',
    clickToDeSelectThenInsert: 'Click to (de)select, then “Insert” adds them to the body.',
    keywordExtractionAiUnavailable: '(keyword extraction — AI unavailable)',
    suggestionGenerationFailedServiceUnavail: 'Suggestion generation failed (service unavailable?).',
    couldNotCopySelectAndCopy: 'Could not copy — select and copy the text manually.',
    visualSuggestions3: 'Visual suggestions',
    numberOfSuggestions: 'Number of suggestions',
    copyAnIdeaThenPasteIt: 'Copy an idea, then paste it into the AI image generator (Media tab).',
    genericTemplateAiUnavailable: '(generic template — AI unavailable)',
    noSuggestionGenerated: 'No suggestion generated.',
    copied: 'Copied ✓',
    copy: 'Copy',
    scheduleThePost: 'Schedule the post',
    schedule2: 'Schedule',
    dateAndTime: 'Date and time',
    theSchedulingDateMustBeIn: 'The scheduling date must be in the future.',
    hookCta: 'Hook / CTA',
    mentions: 'Mentions',
    couldNotPublish: 'Could not publish.',
    designMediaNotFoundInLibrary: 'The design media could not be found in your media library.',
    couldNotFetchDesignMedia: 'Could not fetch the design media.',
    themeSeedQuestions: 'Audience questions',
    themeSeedAngles: 'Suggested angles',
    draftSeededFromCommunityTheme: 'Draft pre-filled from the community theme “{{theme}}”.',
    couldNotLoadCommunityTheme: 'Could not load the theme drawn from comments.',
  },
  contentListPage: {
    untitledLower: 'untitled',
    scheduledV: 'Scheduled · {{v}}',
    openV: 'Open “{{v}}”',
    untitled: 'Untitled',
    archive: 'Archive',
    deletePermanentlyCannotBeUndone: 'Delete permanently (cannot be undone)',
    delete: 'Delete',
    noPlatform: 'No platform',
    none: 'None',
    labelView: '{{label}} view',
    couldNotArchive: 'Could not archive.',
    couldNotDeletePermanently: 'Could not delete permanently.',
    title: 'Title',
    status: 'Status',
    platforms: 'Platforms',
    scheduledUpdated: 'Scheduled / Updated',
    scheduledDate: 'Scheduled date',
    lastUpdated: 'Last updated',
    actions: 'Actions',
    approvalWorkflowDecisionsAndComments: 'Approval workflow, decisions and comments',
    review: 'Review',
    loading: 'Loading…',
    noContentMatchesTheFilters: 'No content matches the filters.',
    content: 'Content',
    writeGetApprovalAndScheduleYour: 'Write, get approval and schedule your posts.',
    newContent: '+ New content',
    searchTitleBody: 'Search (title, body)…',
    searchContent: 'Search content',
    filterByStatus: 'Filter by status',
    allStatuses: 'All statuses',
    filterByPlatform: 'Filter by platform',
    allPlatforms: 'All platforms',
    myContent: 'My content',
    rangetoOfTotal: '–{{rangeTo}} of {{total}}',
    previous: 'Previous',
    next: 'Next',
    contentAnalytics: 'Content analytics',
    analytics: 'Analytics',
    pendingReviewCountAwaitingReview: '{{pending_review_count}} awaiting review',
    openTheReviewQueue: 'Open the review queue',
    someContentIsWaitingForApproval: 'Some content is waiting for approval before scheduling.',
    failedCountFailed: '{{failed_count}} failed',
    viewFailures: 'View failures',
    somePostsFailedAndNeedA: 'Some posts failed and need a retry.',
    publishedCountPublished: '{{published_count}} published',
    vOfYourContentIsPublished: '{{v}}% of your content is published.',
    noContentYet: 'No content yet.',
    nothingScheduled: 'Nothing scheduled',
    createContent: 'Create content',
    scheduleContentToKeepASteady: 'Schedule content to keep a steady presence.',
    scheduledCountScheduled: '{{scheduled_count}} scheduled',
    upcomingScheduledPosts: 'Upcoming scheduled posts.',
    vLeads: '{{v}} leads',
    filterThisPlatform: 'Filter this platform',
    contentItemSTargetV: 'content item(s) target {{v}}.',
    contentsTodayToday: '{{contents_today}} today',
    createdThisWeekContentsThisMonth: 'created this week · {{contents_this_month}} this month.',
    archiveTheContent: 'Archive the content',
    archiveTheContentV: 'Archive the content “{{v}}”?',
    deletePermanently: 'Delete permanently',
    permanentlyDeleteTheContentVThis: 'Permanently delete the content “{{v}}”? This cannot be undone: the content is removed from the database (net-zero cleanup).',
    grid: 'Grid',
    table: 'Table',
  },
  contentReviewPage: {
    contentNotFound: 'Content not found.',
    couldNotLoadThisContent: 'Could not load this content.',
    couldNotLoadTheApprovalState: 'Could not load the approval state.',
    stepApproved: 'Step approved.',
    contentRejected: 'Content rejected.',
    theActionFailed: 'The action failed.',
    contentReview: 'Content review',
    approvalWorkflowPerStepDecisionsComments: 'Approval workflow, per-step decisions, comments and guest links.',
    openInTheEditor: 'Open in the editor',
    loading: 'Loading…',
    theApprovalWorkflowIsNotDeployed: 'The approval workflow is not deployed on this environment yet. Step states will show as soon as it is deployed.',
    untitled: 'Untitled',
    noApprovalWorkflowConfiguredForThis: 'No approval workflow configured for this content.',
    configure: 'Configure',
    approvalWorkflow: 'Approval workflow',
    unanimous: 'Unanimous — ',
    oneOf: 'One of — ',
    guest: '(guest)',
    youAlreadyVotedOnThisStep: 'You already voted on this step.',
    commentOptional: 'Comment (optional)',
    approve: 'Approve',
    reject: 'Reject',
    guestLinkCreatedAndCopied: 'Guest link created and copied.',
    guestLinkCreated: 'Guest link created.',
    creatingTheLinkFailed: 'Creating the link failed.',
    revokeThisLink: 'Revoke this link?',
    theLinkWillStopWorkingImmediately: 'The link will stop working immediately.',
    revoke: 'Revoke',
    linkRevoked: 'Link revoked.',
    revocationFailed: 'Revocation failed.',
    guestLinks: 'Guest links',
    shareAnApprovalLinkWithSomeone: 'Share an approval link with someone without an account. The link is only shown once.',
    onlyOwnerOrAdminGuestLinks: 'Only the owner or an administrator can create or revoke a guest link.',
    newLinkCopyItNow: 'New link (copy it now)',
    emailOptional: 'Email (optional)',
    creating: 'Creating…',
    createAGuestLink: 'Create a guest link',
    guest2: 'Guest',
    sendingTheCommentFailed: 'Sending the comment failed.',
    updateFailed: 'Update failed.',
    deleteThisComment: 'Delete this comment?',
    thisActionIsPermanent: 'This action is permanent.',
    delete: 'Delete',
    deletionFailed: 'Deletion failed.',
    comments: 'Comments',
    noCommentsMentionATeammateWith: 'No comments. Mention a teammate with @.',
    member: 'Member',
    resolved: '· resolved',
    reopen: 'Reopen',
    resolve: 'Resolve',
    del: 'Del.',
    addACommentMention: 'Add a comment… (@mention)',
    sending: 'Sending…',
    comment: 'Comment',
  },
  diagramStudioPage: {
    diagramSavedToTheMediaLibrary: 'Diagram saved to the media library.',
    couldNotSaveTheDiagramMedia: 'Could not save the diagram (media library unavailable?).',
    diagramStudio: 'Diagram studio',
    createDiagramsDrawIoAndSave: 'Create diagrams (draw.io) and save them to your media library — the image stays re-editable.',
    newDiagram: '📊 New diagram',
    close: 'Close',
    diagramTitle: 'Diagram title',
    eGCampaignArchitecture: 'E.g. Campaign architecture',
    noDiagramsYetClickNewDiagram: 'No diagrams yet. Click “New diagram” to get started.',
    previewOfDiagramV: 'Preview of diagram {{v}}',
    open: 'Open',
    edit: '✏️ Edit',
    diagram: 'Diagram',
    saved: 'saved',
  },
  dialogs: {
    rejectTheContent: 'Reject the content',
    cancel: 'Cancel',
    rejecting: 'Rejecting…',
    reject: 'Reject',
    sendThisContentBackToDraft: 'Send this content back to draft for revision. A reason helps the author fix it.',
    rejectionReasonOptional: 'Rejection reason (optional)',
    eGAddACallTo: 'E.g. add a call to action, fix the 2nd sentence…',
    confirm: 'Confirm',
  },
  ideasFeedPage: {
    saved: 'Saved',
    archived: 'Archived',
    justNow: 'just now',
    minutesMAgo: '{{minutes}}m ago',
    hoursHAgo: '{{hours}}h ago',
    daysDAgo: '{{days}}d ago',
    untitled: '(Untitled)',
    idea: 'Idea',
    editorialScoreScore: 'Editorial score: {{score}}',
    source: '🔗 Source',
    edit: 'Edit',
    archive: 'Archive',
    delete: 'Delete',
    newIdea: 'New idea',
    captureFromUrl: 'Capture from URL',
    editTheIdea: 'Edit the idea',
    httpsExampleComPost: 'https://example.com/post',
    title: 'Title',
    eG2024ContentMarketingTrend: 'E.g. 2024 content marketing trend',
    description: 'Description',
    describeTheIdea: 'Describe the idea…',
    sourceOptionalUrl: 'Source (optional URL)',
    httpsExampleCom: 'https://example.com',
    imageOptionalUrl: 'Image (optional URL)',
    httpsExampleComImageJpg: 'https://example.com/image.jpg',
    niche: 'Niche',
    eGFashionTechnology: 'E.g. Fashion, Technology',
    tagsCommaSeparated: 'Tags (comma-separated)',
    viralTrendEngagement: 'viral, trend, engagement',
    status: 'Status',
    new: 'New',
    editorialScore0100: 'Editorial score (0-100)',
    cancel: 'Cancel',
    saving: 'Saving…',
    save: 'Save',
    theIdeaFeedIsNotAvailable: 'The idea feed is not available on this environment.',
    errorStatusMessage: 'Error {{status}}: {{message}}',
    anErrorOccurredWhileLoading: 'An error occurred while loading.',
    theUrlIsRequired: 'The URL is required.',
    saveError: 'Save error',
    errorWhileArchiving: 'Error while archiving',
    errorWhileDeleting: 'Error while deleting',
    ideaFeed: 'Idea feed',
    inspirationAndSwipeCaptureForYour: 'Inspiration and swipe-capture for your creative pipeline',
    searchByTitleOrDescription: 'Search by title or description…',
    captureUrl: 'Capture URL',
    all: 'All',
    filter: 'Filter…',
    tag: 'Tag',
    loadingIdeas: 'Loading ideas…',
    noIdeasYet: 'No ideas yet.',
    noResults: 'No results.',
    captureYourFirstIdea: 'Capture your first idea',
    deleteTheIdea: 'Delete the idea?',
    areYouSureYouWantTo: 'Are you sure you want to delete “',
    untitled2: 'Untitled',
    thisActionCannotBeUndone: '”? This action cannot be undone.',
  },
  postPreview: {
    selectAPlatformToPreviewThe: 'Select a platform to preview the post.',
    serverRenderedPreviewTokensSubstitutedCo: 'Server-rendered preview (tokens substituted, constraints applied) — identical to the published post.',
    localPreviewServerRenderingIsUnavailable: 'Local preview (server rendering is unavailable or in progress).',
    serverPreview: 'server preview',
    localPreview: 'local preview',
    yourPage: 'Your page',
    vJustNow: '{{v}} · just now',
    emptyText: '(empty text)',
    tooLong: ' — too long',
  },
  richContentStudioPage: {
    errorStatus: 'Error {{status}}',
    infographic: 'Infographic',
    pngPdfVisualReadyToPublish: 'PNG + PDF visual ready to publish',
    animatedGif: 'Animated GIF',
    shortAnimationMp4Optional: 'Short animation, MP4 optional',
    hookCaptionHashtagsPerPlatform: 'Hook + caption + hashtags per platform',
    french: 'French',
    english: 'English',
    spanish: 'Spanish',
    arabic: 'Arabic',
    portuguese: 'Portuguese',
    german: 'German',
    aiTextGeneration: 'AI text generation',
    infographicRendering: 'infographic rendering',
    mp4Export: 'MP4 export',
    imageGeneration: 'image generation',
    someFeaturesAreNotAvailableV: 'Some features are not available: {{v}}. Contact your administrator.',
    typeOfContentToGenerate: 'Type of content to generate',
    imageWidthHeightOrientation: 'Image {{width}}×{{height}} ({{orientation}})',
    videoWidthHeightOrientation: 'Video {{width}}×{{height}} ({{orientation}})',
    couldNotLoadThePlatforms: 'Could not load the platforms.',
    generationFailed: 'Generation failed.',
    enterATopicForTheGeneration: 'Enter a topic for the generation.',
    couldNotCopySelectAndCopy: 'Could not copy — select and copy the text manually.',
    richContentStudio: 'Rich content studio',
    generateAnInfographicAnAnimatedGif: 'Generate an infographic, an animated GIF or a multi-platform post pack with AI.',
    theRichContentStudioIsNot: 'The rich content studio is not deployed on this environment yet. Try again later.',
    contentType: 'Content type',
    topic: 'Topic',
    eGTheLaunchOfOur: 'E.g. the launch of our new small-business offer',
    dictate: 'Dictate',
    creativeBriefOptional: 'Creative brief (optional)',
    steersTheToneAudienceKeywordsAnd: 'Steers the tone, audience, keywords and visuals produced.',
    targetPlatforms: 'Target platforms',
    platform: 'Platform',
    noPlatformAvailableYet: 'No platform available yet.',
    language: 'Language',
    secondLanguageBilingualOptional: 'Second language (bilingual, optional)',
    none: 'None',
    brandKit: 'Brand kit',
    subtitleOptional: 'Subtitle (optional)',
    signatureBylineOptional: 'Signature / byline (optional)',
    designTemplateOptional: 'Design template (optional)',
    none2: 'None',
    numberOfImagesFrames: 'Number of images (frames)',
    moreFramesSmootherAnimationLongerTo: 'More frames = smoother animation, longer to generate',
    alsoGenerateAnMp4Version: 'Also generate an MP4 version',
    thisGenerationUsesAiCredits: 'This generation uses AI credits.',
    generating: 'Generating…',
    generate: 'Generate',
    generating2: 'Generating…',
    enterATopicAndClickGenerate: 'Enter a topic and click “Generate” to see the infographic.',
    enterATopicAndClickGenerate2: 'Enter a topic and click “Generate” to see the animated GIF.',
    enterATopicAndClickGenerate3: 'Enter a topic and click “Generate” to see the post pack.',
    platform2: 'Platform:',
    infographicLanguage: 'Infographic ({{language}})',
    previewUnavailable: 'Preview unavailable',
    downloadPng: 'Download PNG',
    downloadPdf: 'Download PDF',
    animatedGifLanguage: 'Animated GIF ({{language}})',
    downloadGif: 'Download GIF',
    downloadMp4: 'Download MP4',
    noPostPackGenerated: 'No post pack generated.',
    callToActionCta: 'Call to action: {{cta}}',
    copied: 'Copied ✓',
    copyTheCaption: 'Copy the caption',
    sendToTheComposer: 'Send to the composer',
    schedule: 'Schedule',
    postPack: 'Post pack',
  },
  sourceStudioPage: {
    theSource: 'the source',
    mindMap: 'Mind map',
    couldNotLoadTheSetV: 'Could not load the set: {{v}}',
    setNameCreated: 'Set “{{name}}” created.',
    couldNotCreateV: 'Could not create: {{v}}',
    nameAdded: '“{{name}}” added.',
    couldNotAddV: 'Could not add: {{v}}',
    linkAdded: 'Link added.',
    thisTypeOfLinkEG: 'This type of link (e.g. video) is not supported yet.',
    textAdded: 'Text added.',
    sourceDeleted: 'Source deleted.',
    couldNotDeleteV: 'Could not delete: {{v}}',
    couldNotAnswerV: 'Could not answer: {{v}}',
    couldNotCreateTheAudioOverview: 'Could not create the audio overview: {{v}}',
    sourceStudio: '📚 Source Studio',
    gatherYourSourcesDocumentsLinksText: 'Gather your sources (documents, links, text), then generate summaries, a chat grounded in your sources and a two-voice audio overview — NotebookLM-style.',
    loading: 'Loading…',
    refresh: 'Refresh',
    sourceStudioIsComingSoonYour: 'Source Studio is coming soon. Your source sets will appear here once it is enabled.',
    couldNotLoadSourceStudioMessage: 'Could not load Source Studio: {{message}}',
    sourceSets: 'Source sets',
    sourceSet: 'Source set',
    activeSet: 'Active set',
    newSetName: 'New set name…',
    newSetName2: 'New set name',
    create: 'Create',
    addASource: 'Add a source',
    uploadADocument: 'Upload a document',
    pdfWordTextTheDocumentIs: 'PDF, Word, text… The document is indexed and becomes searchable.',
    https: 'https://…',
    sourceUrl: 'Source URL',
    add: 'Add',
    pasteYourTextHere: 'Paste your text here…',
    sourceText: 'Source text',
    adding: 'Adding…',
    addTheText: 'Add the text',
    sources: 'Sources',
    loadingSources: 'Loading sources…',
    noSourcesYetAddADocument: 'No sources yet. Add a document, a link or some text above.',
    untitled: '(untitled)',
    deleteV: 'Delete {{v}}',
    delete: 'Delete',
    createASourceSetToGet: 'Create a source set to get started',
    aSetGroupsYourDocumentsLinks: 'A set groups your documents, links and texts. The AI relies only on these sources to generate summaries and answer your questions.',
    generate: 'Generate',
    summariesGroundedInTheActiveSet: 'Summaries grounded in the active set\'s sources.',
    generating: 'Generating…',
    addAtLeastOneSourceTo: 'Add at least one source to enable generation.',
    yourSourcesAreBeingProcessedGeneration: 'Your sources are being processed — generation may be partial until they are ready.',
    couldNotGenerateGenerror: 'Could not generate: {{genError}}',
    chatWithTheSources: 'Chat with the sources',
    askAQuestionTheAnswerIs: 'Ask a question: the answer is grounded in your sources, with supporting excerpts.',
    excerpts: 'Excerpts',
    yourQuestion: 'Your question…',
    question: 'Question',
    ask: 'Ask',
    addASourceToEnableThe: 'Add a source to enable the chat.',
    audioOverview: 'Audio overview',
    audioOverview2Voices: 'Audio overview (2 voices)',
    aTwoVoiceDialogueThatSums: 'A two-voice dialogue that sums up your sources.',
    generateTheAudioOverview: 'Generate the audio overview',
    yourBrowserDoesNotSupportAudio: 'Your browser does not support audio playback.',
    ttsNotConfiguredKeyRequired: 'TTS not configured (key required)',
    speechSynthesisIsNotConfiguredYet: 'Speech synthesis is not configured yet. The script was generated below; the audio will be available once a TTS key is provided.',
    generatedScript: 'Generated script',
    addASourceToGenerateAn: 'Add a source to generate an audio overview.',
    file: 'File',
    link: 'Link',
    text: 'Text',
    you: 'You',
    ai: 'AI',
    deleteTheSet: 'Delete the set',
    deleteTheSetTitle: 'Delete the set?',
    deleteTheSetMessage: 'The set “{{name}}”, its sources and their index will be permanently deleted.',
    setNameDeleted: 'Set “{{name}}” deleted.',
  },
  templatePicker: {
    advertising: 'Advertising',
    videoScript: 'Video script',
    couldNotLoadTheTemplates: 'Could not load the templates.',
    thisTemplateIsReadOnlyAnd: 'This template is read-only and cannot be deleted.',
    couldNotDeleteTheTemplate: 'Could not delete the template.',
    contentTemplateOptional: 'Content template (optional)',
    loadingTemplates: 'Loading templates…',
    noTemplate: 'No template',
    freeGenerationNoImposedStructure: 'Free generation, no imposed structure.',
    global: 'Global',
    toneTone: 'Tone: {{tone}}',
    newTemplate: '＋ New template',
    deleteTheTemplateTitle: 'Delete the template {{title}}',
    deleteThisTemplate: 'Delete this template',
    enterANameAndAtLeast: 'Enter a name and at least one named section.',
    insufficientRightsToCreateATemplate: 'Insufficient rights to create a template.',
    couldNotCreateTheTemplate: 'Could not create the template.',
    newTemplate2: 'New template',
    name: 'Name',
    eGB2bCaseStudy: 'E.g. B2B case study',
    contentType: 'Content type',
    post: 'post',
    format: 'Format',
    text: 'text',
    tone: 'Tone',
    professional: 'professional',
    sectionsInOrder: 'Sections (in order)',
    addASection: '＋ Add a section',
    sectionNameEGHook: 'Section name (e.g. Hook)',
    moveTheSectionUp: 'Move the section up',
    moveTheSectionDown: 'Move the section down',
    deleteTheSection: 'Delete the section',
    instructionWhatThisSectionShouldContain: 'Instruction: what this section should contain…',
    creating: 'Creating…',
    createTemplate: 'Create template',
    cancel: 'Cancel',
    publishing: 'Publishing',
    article: 'Article',
    thread: 'Thread',
    carousel: 'Carousel',
    story: 'Story',
    newsletter: 'Newsletter',
    blog: 'Blog',
    email: 'Email',
  },
  unifiedStudioPage: {
    errorStatus: 'Error {{status}}',
    post: 'Post',
    hookCaptionHashtagsPerPlatform: 'Hook + caption + hashtags per platform',
    carousel: 'Carousel',
    slidesReadyToPublish: 'Slides ready to publish',
    infographic: 'Infographic',
    pngPdfVisual: 'PNG/PDF visual',
    shortAnimation: 'Short animation',
    reel: 'Reel',
    videoEditMediaIds: 'Video edit (media IDs)',
    animatedGif: 'Animated GIF',
    french: 'French',
    english: 'English',
    spanish: 'Spanish',
    arabic: 'Arabic',
    portuguese: 'Portuguese',
    german: 'German',
    richContentStudio: 'Rich content studio',
    videoAudioStudio: 'Video & audio studio',
    carouselEditor: 'Carousel editor',
    graphicStudio: 'Graphic studio',
    diagramStudio: 'Diagram studio',
    unifiedStudio: 'Unified studio',
    enterATopicForTheGeneration: 'Enter a topic for the generation.',
    enableAtLeastOneAssetType: 'Enable at least one asset type to produce.',
    okAssetSProducedFailedUnavailable: '{{ok}} asset(s) produced, {{failed}} unavailable.',
    okAssetSProduced: '{{ok}} asset(s) produced.',
    noAssetCouldBeProduced: 'No asset could be produced.',
    generationFailed: 'Generation failed.',
    couldNotCopySelectAndCopy: 'Could not copy — select and copy the text manually.',
    noUsablePostToSendTo: 'No usable post to send to the composer.',
    noUsableMediaToSendTo: 'No usable media to send to the composer.',
    sessionExpiredSignInAgainTo: 'Session expired — sign in again to schedule a draft.',
    nothingToSchedule: 'Nothing to schedule.',
    unifiedStudioV: 'Unified studio — {{v}}',
    labelDraftCreatedFromTheUnified: '{{label}} draft created from the Unified studio',
    draftCreatedLengthPostSFind: 'Draft created — {{length}} post(s). Find it in the calendar.',
    creatingTheDraftFailed: 'Creating the draft failed.',
    noMediaToSchedule: 'No media to schedule.',
    oneBriefOneClickAllYour: 'One brief, one click, all your assets.',
    theUnifiedStudioIsNotDeployed: 'The Unified studio is not deployed on this environment yet. Try again later.',
    aSingleBriefOneClickProduce: 'A single brief, one click: produce a post, carousel, infographic, GIF and reel at once — each asset stays a draft.',
    specializedStudios: 'Specialized studios:',
    topic: 'Topic',
    eGTheLaunchOfOur: 'E.g. the launch of our new small-business offer',
    creativeBriefOptional: 'Creative brief (optional)',
    steersTheToneAudienceKeywordsAnd: 'Steers the tone, audience, keywords and visuals produced.',
    targetPlatforms: 'Target platforms',
    noPlatformAvailableYet: 'No platform available yet.',
    language: 'Language',
    secondLanguageBilingualOptional: 'Second language (bilingual, optional)',
    none: 'None',
    brandKit: 'Brand kit',
    assetsToProduce: 'Assets to produce',
    reelMediaIds: 'Reel media IDs',
    mediaLibraryIdsCommaSeparatedIn: 'Media library IDs, comma-separated, in order',
    id1Id2Id3: 'id1, id2, id3',
    thisGenerationUsesAiCreditsThe: 'This generation uses AI credits. The produced assets stay drafts.',
    generating: 'Generating…',
    generateAll: 'Generate all',
    generating2: 'Generating…',
    enterATopicEnableTheAssets: 'Enter a topic, enable the assets you want, then click “Generate all” to see the results.',
    noAssetRequested: 'No asset requested.',
    generated: 'Generated',
    notConfigured: 'Not configured',
    thisAssetCouldNotBeProduced: 'This asset could not be produced on this environment.',
    sendToTheComposer: 'Send to the composer',
    scheduleDraft: 'Schedule (draft)',
    noPostGenerated: 'No post generated.',
    sendEverythingToTheComposer: 'Send everything to the composer',
    scheduleEverythingDraft: 'Schedule everything (draft)',
    callToActionCta: 'Call to action: {{cta}}',
    copied: 'Copied ✓',
    copyTheCaption: 'Copy the caption',
    schedule: 'Schedule',
    noSlideGenerated: 'No slide generated.',
    slideV: 'Slide {{v}}',
    previewUnavailable: 'Preview unavailable',
    reelUnavailable: 'Reel unavailable.',
    previewUnavailable2: 'Preview unavailable.',
    download: 'Download',
    downloadThePdf: 'Download the PDF',
    gif: 'GIF',
    postPack: 'Post pack',
  },
  videoStudioPage: {
    transcription: 'Transcription',
    clipsRepurpose: 'Clips (repurpose)',
    voiceOver: 'Voice-over',
    clonedVoice: 'Cloned voice',
    avatar: 'Avatar',
    reel: 'Reel',
    featureNotConfigured: 'Feature not configured —',
    mediaIdMediaLibrary: 'Media ID (media library)',
    orAPublicUrlNextTo: 'OR a public URL next to it',
    idOfATenantVideoAudio: 'ID of a tenant video/audio media',
    publicMediaUrl: 'Public media URL',
    httpsVideoMp4: 'https://…/video.mp4',
    videoAudioStudio: 'Video & audio studio',
    provideAMediaIdOrA: 'Provide a media ID or a URL.',
    transcriptionComplete: 'Transcription complete.',
    provideAMediaIdOrA2: 'Provide a media ID or a URL for the source video.',
    totalClipSGenerated: '{{total}} clip(s) generated.',
    enterTheTextToSynthesize: 'Enter the text to synthesize.',
    voiceOverGenerated: 'Voice-over generated.',
    enterTheTextAndTheCloned: 'Enter the text and the cloned voice ID.',
    clonedVoiceGenerated: 'Cloned voice generated.',
    provideAPromptAnImageOr: 'Provide a prompt, an image or an audio (media ID).',
    avatarGenerated: 'Avatar generated.',
    provideAtLeastOneVideoClip: 'Provide at least one video clip ID (comma-separated).',
    reelAssembled: 'Reel assembled.',
    theOperationFailed: 'The operation failed.',
    transcriptionSubtitledRepurposedClipsVoi: 'Transcription, subtitled repurposed clips, voice-over, cloned voice, avatar and reels — real renders from the media library, never simulated.',
    productionType: 'Production type',
    languageOptional: 'Language (optional)',
    frEn: 'fr, en…',
    clipDurationS: 'Clip duration (s)',
    maxNumberOfClips: 'Max number of clips',
    burnSubtitlesIntoTheClips: 'Burn subtitles into the clips',
    text: 'Text',
    theTextToReadAloud: 'The text to read aloud…',
    voiceOptional: 'Voice (optional)',
    providerVoiceName: 'provider voice name',
    clonedVoiceId: 'Cloned voice ID',
    providerVoiceId: 'provider voice_id',
    promptOptional: 'Prompt (optional)',
    describeTheSceneTheAvatarS: 'Describe the scene / the avatar\'s script…',
    imageMediaIdOptional: 'Image (media ID, optional)',
    idOfATenantImage: 'ID of a tenant image',
    audioMediaIdOptional: 'Audio (media ID, optional)',
    idOfATenantAudio: 'ID of a tenant audio',
    videoClipIds: 'Video clip IDs',
    commaSeparatedInOrder: 'Comma-separated, in order',
    id1Id2Id3: 'id1, id2, id3',
    voiceOverAudioMediaIdOptional: 'Voice-over (audio media ID, optional)',
    processing: 'Processing…',
    run: 'Run',
    downloadTheSrt: 'Download the SRT',
    noText: '(no text)',
    totalClipSGenerated2: '{{total}} clip(s) generated',
    mediaUnavailable: 'Media unavailable',
    generatedAudio: 'Generated audio',
    download: 'Download',
    mediaSavedIdId: 'Media saved (id {{id}}).',
    avatarVideo: 'Avatar video',
    assembledReel: 'Assembled reel',
    reelSavedIdId: 'Reel saved (id {{id}}).',
  },
  widgets: {
    drafts: 'Drafts',
    underReview: 'Under review',
    approved: 'Approved',
    scheduled: 'Scheduled',
    published: 'Published',
    vValueOfSafemaxPcttext: '{{v}}{{value}} of {{safeMax}} ({{pctText}}%)',
    trendOverLengthPointsLatestLast: 'Trend over {{length}} points (latest: {{last}})',
    editorialPipeline: 'Editorial pipeline',
    sumInFlow: '{{sum}} in flow',
    filterLabelCount: 'Filter: {{label}} ({{count}})',
    contentIndicators: 'Content indicators',
    totalContent: 'Total content',
    published2: '% published',
    createdThisWeek: 'Created this week',
    contentCreatedPerDayOver7: 'Content created per day over 7 days (loaded items): {{v}}',
    filterLabel: 'Filter: {{label}}',
  },
  singleStepReview: {
    statusDraft: 'Draft',
    statusPendingReview: 'In review',
    statusApproved: 'Approved',
    statusScheduled: 'Scheduled',
    statusPublished: 'Published',
    statusArchived: 'Archived',
    statusFailed: 'Failed',
    actionReservedToReviewers: 'Action reserved for reviewers.',
    actionFailed: 'The action failed.',
    selfApprovalAllowed: 'Self-approval allowed.',
    separationOfDutiesRestored: 'Separation of duties restored.',
    insufficientRightsToChangePolicy: 'Insufficient rights to change the approval policy.',
    couldNotChangePolicy: 'Could not change the approval policy.',
    singleStepReview: 'Single-step review',
    review: 'Review',
    noChainConfiguredSingleDecision: 'No approval chain is configured: a single reviewer decision is enough.',
    configureAChain: 'Configure a chain',
    contentSubmittedForReview: 'Content submitted for review.',
    sending: 'Sending…',
    submitForReview: 'Submit for review',
    decisionReservedToReviewers: 'Decision reserved for reviewers (owner, admin, manager, publisher).',
    commentReasonRequiredToReject: 'Comment / reason (required to reject)',
    notePlaceholder: 'E.g. tone to adjust, visual to replace…',
    separationOfDutiesAuthorCannotApprove: 'Separation of duties: the author cannot approve their own content.',
    allowSelfApprovalBelowOrAnotherReviewer: 'Allow self-approval below, or have another reviewer approve.',
    anotherReviewerMustApprove: 'Another reviewer must approve.',
    contentApproved: 'Content approved.',
    approving: 'Approving…',
    approve: 'Approve',
    giveAReasonToReject: 'Give a reason to reject.',
    contentRejectedBackToDraft: 'Content rejected (back to draft).',
    rejecting: 'Rejecting…',
    reject: 'Reject',
    allowSelfApprovalAuthorCanApprove: 'Allow self-approval (the author can approve their own content)',
    decisionHistory: 'Decision history',
    noDecisionYet: 'No decision yet.',
    approvedMark: '✓ Approved',
    rejectedMark: '✕ Rejected',
    byYou: 'by you',
    byTheClientLink: 'by the client (link)',
    byName: 'by {{name}}',
  },
  clientPortalLinksPanel: {
    stateActive: 'Active',
    stateExpired: 'Expired',
    stateRevoked: 'Revoked',
    actionInvited: 'Link created',
    actionView: 'Viewed',
    actionApprove: 'Approved',
    actionReject: 'Rejected',
    actionRequestChanges: 'Changes requested',
    actionComment: 'Comment',
    reservedToOwnerOrAdmin: 'Reserved for the owner or an administrator.',
    portalUnavailableOnThisEnvironment: 'The client portal is not available on this environment.',
    couldNotLoadClientLinks: 'Could not load the client links.',
    clientLinkCreatedAndCopied: 'Client link created and copied.',
    clientLinkCreatedCopyBelow: 'Client link created — copy it below.',
    clientLinkCreationFailed: 'Failed to create the client link.',
    revokeThisClientLink: 'Revoke this client link?',
    clientWillNoLongerOpenThisLink: 'The client will no longer be able to open or use this link.',
    revoke: 'Revoke',
    clientLinkRevoked: 'Client link revoked.',
    revocationFailed: 'Revocation failed.',
    shareWithClient: 'Share with the client',
    noAccountLinkExplanation: 'A no-account link, limited to this content, so your client can approve it, request changes or comment. The link is shown only once; no e-mail is sent.',
    clientOnlySeesContentInReview: 'The client only sees content in review: submit this content for review so it appears in their portal.',
    clientLinkCopyItNow: 'Client link (copy it now)',
    copied: 'Copied ✓',
    copyTheLink: 'Copy the link',
    labelOptional: 'Label (optional)',
    labelPlaceholder: 'E.g. Acme client — fall campaign',
    expiration: 'Expiration',
    creating: 'Creating…',
    onlyOwnerOrAdminCanCreateOrRevoke: 'Only the owner or an administrator can create or revoke a client link.',
    noClientLinkForThisContent: 'No client link for this content.',
    clientLink: 'Client link',
    expiresOnLastAccess: 'expires {{expires}} · last access {{lastUsed}}',
    activity: 'Activity',
    noActivity: 'No activity.',
  },
};

export default en;
